import { Container, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'src/components/Header';
import Page from 'src/components/Page';
import { useCreateStoreMutation, useGetTimezonesQuery } from 'src/features/store/api/storeApi';
import type { StoreFormValues } from 'src/features/store/types/store.types';
import {
  emptyStoreFormValues,
  toStoreCrudPayload,
} from 'src/features/store/utils/storeFormPayload';
import useAppInsightPageView from 'src/hooks/useAppInsightPageView';
import { Theme } from 'src/theme';
import { getSelectedClientId } from 'src/utils/client';
import { truncate } from 'src/utils/common';
import { isExoApiError } from 'src/utils/errorHandlers';
import StoreForm from './StoreForm';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: 100,
  },
}));

const breadcrumbs = [
  {
    name: 'Manage Stores',
    path: '/management/stores',
  },
];

const StoreCreatePage: FC = () => {
  useAppInsightPageView({ name: 'ExoAdmin-Create Store' });
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const clientId = getSelectedClientId();
  const { data: timezones = [] } = useGetTimezonesQuery();
  const [createStore, { isLoading }] = useCreateStoreMutation();

  const handleSubmit = async (values: StoreFormValues): Promise<void> => {
    try {
      await createStore(toStoreCrudPayload(values, clientId)).unwrap();
      enqueueSnackbar(`${truncate(values.name)} has been created.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      navigate('/app/management/stores');
    } catch (error) {
      enqueueSnackbar(
        isExoApiError(error) ? error.data.detail : `Failed to create ${truncate(values.name)}.`,
        { variant: 'error', autoHideDuration: 3000 },
      );
    }
  };

  return (
    <Page className={classes.root} title="Create Store" exoBgLogo={true}>
      <Container maxWidth={false}>
        <Header
          breadcrumbs={breadcrumbs}
          pageTitle="Add New Store"
          showCancelButton={true}
          cancelLink="/app/management/stores"
          mainHeader="Management"
        />
        <StoreForm
          mode="create"
          initialValues={emptyStoreFormValues}
          timezones={timezones}
          submitLabel="Create Store"
          isSubmitting={isLoading}
          onSubmit={handleSubmit}
        />
      </Container>
    </Page>
  );
};

export default StoreCreatePage;
